<template>
    <div>
        <limitless-page-header :loading="busy_loading" :title="'Varumärken'" :sub-title="brand_name" :title-id="brand_id">
            <template #header-elements>
                <limitless-page-header-button :route="{ name: 'productbrands.list' }" icon="icon-circle-left2">Tillbaka</limitless-page-header-button>
            </template>
        </limitless-page-header>

        <div class="content">
            <tb-skeleton v-if="busy_loading" :height="100" :theme="'opacity'" :shape="'radius'" bg-color="#dcdbdc"></tb-skeleton>

            <div v-else-if="!busy_loading && brand">
                <div class="card">
                    <div class="card-body">
                        <ValidationObserver ref="brandform">
                            <form @submit.prevent="saveBrand">
                                <div class="row">
                                    <div class="col-lg-6">
                                        <limitless-tabs
                                            :tabs="[{ label: 'Svenska' }]"
                                            class="mb-3"
                                        >
                                            <template #tab-1>
                                                <div class="row">
                                                    <div class="col-lg-12">
                                                        <ValidationProvider vid="translations.SV.name" name="Namn" rules="required" v-slot="{ errors }">
                                                            <div class="form-group">
                                                                <label>Namn <span class="text-danger">*</span></label>
                                                                <input type="text" class="form-control" v-model="brand.translations.SV.name" v-bind:class="{ 'border-danger':errors[0] }">
                                                                <span class="validation-invalid-label">{{ errors[0] }}</span>
                                                            </div>
                                                        </ValidationProvider>
                                                    </div>
                                                </div>
                                                <ValidationProvider vid="'translations.SV.description'" name="Beskrivning" v-slot="{ errors }">
                                                    <div class="form-group">
                                                        <label>Beskrivning</label>
                                                        <simple-text-editor
                                                            v-model="brand.translations.SV.description"
                                                            :toolbar="[
                                                                'bold',
                                                                'italic',
                                                                'strike',
                                                                'underline',
                                                                'rawmode',
                                                            ]"
                                                        ></simple-text-editor>
                                                        <span class="validation-invalid-label">{{ errors[0] }}</span>
                                                    </div>
                                                </ValidationProvider>
                                            </template>
                                        </limitless-tabs>
                                    </div>
                                    <div class="col-lg-6">
                                        <limitless-tabs
                                            :tabs="language_tabs"
                                            class="mb-3"
                                        >
                                            <template v-for="lang in language_tabs" v-slot:[lang.slot]>
                                                <div :key="'tab-' + lang.id">
                                                    <div class="row">
                                                        <div class="col-lg-12">
                                                            <ValidationProvider :vid="'translations.' + lang.id + '.name'" name="Namn" v-slot="{ errors }">
                                                                <div class="form-group">
                                                                    <label>Namn</label>
                                                                    <input type="text" class="form-control" v-model="brand.translations[lang.id].name" v-bind:class="{ 'border-danger':errors[0] }">
                                                                    <span class="validation-invalid-label">{{ errors[0] }}</span>
                                                                </div>
                                                            </ValidationProvider>
                                                        </div>
                                                    </div>
                                                    <ValidationProvider :vid="'translations.' + lang.id + '.description'" name="Beskrivning" v-slot="{ errors }">
                                                        <div class="form-group">
                                                            <label>Beskrivning</label>
                                                            <simple-text-editor
                                                                v-model="brand.translations[lang.id].description"
                                                                :toolbar="[
                                                                    'bold',
                                                                    'italic',
                                                                    'strike',
                                                                    'underline',
                                                                    'rawmode',
                                                                ]"
                                                            ></simple-text-editor>
                                                            <span class="validation-invalid-label">{{ errors[0] }}</span>
                                                        </div>
                                                    </ValidationProvider>
                                                    <div class="text-right">
                                                        <limitless-button
                                                            :ref="'btntranslate' + lang.id.toLowerCase()"
                                                            type="button"
                                                            class="btn"
                                                            btn-class="btn-light"
                                                            @click="translateTexts(lang.id)"
                                                            :busy="busy_translating"
                                                            :saved-text="'Översatt!'"
                                                        >
                                                            <i class="icon-magic-wand mr-2"></i>Översätt (med AI)
                                                        </limitless-button>
                                                    </div>
                                                </div>
                                            </template>
                                        </limitless-tabs>
                                    </div>
                                </div>
                                <div>
                                    <div style="font-weight: bold;">OBS! Loggan ska laddas upp som PNG med genomskinlig bakgrund.</div>
                                    <div style="max-width: 210px;">
                                        <mediabank-selector
                                            v-model="brand.logo_id"
                                            btn-class="btn-sm btn-secondary"
                                            btn-text="Välj logotyp"
                                            btn-icon="icon-image2"
                                            :category-id="1012"
                                            category-locked
                                            thumbnail
                                            upload
                                        ></mediabank-selector>
                                    </div>
                                </div>
                            </form>
                        </ValidationObserver>
                    </div>
                    <div class="card-footer">
                        <div class="d-flex justify-content-end">
                            <limitless-button v-if="brand.id > 0" ref="btndeletebrand" type="button" @click="deleteBrand" :busy="busy_saving" icon="icon-cross2" btn-class="btn-danger" class="mr-auto">Radera</limitless-button>
                            <limitless-button ref="btnsavebrand" type="button" @click="saveBrand" :busy="busy_saving" icon="icon-floppy-disk">Spara</limitless-button>
                        </div>
                    </div>
                </div>

            </div>
        </div> <!-- /content -->
    </div>
</template>

<script>
import apiClient from '@services/ApiClient';
import LimitlessPageHeader from '@components/Layout/partials/LimitlessPageHeader';
import LimitlessPageHeaderButton from '@components/Layout/partials/LimitlessPageHeaderButton';
import LimitlessButton from '@components/Limitless/LimitlessButton';
import getError from '@services/ErrorHelper';
import BackendRepository from '@services/repositories/BackendRepository';
import SimpleTextEditor from '@components/SimpleTextEditor.vue';
import LimitlessTabs from '@components/Limitless/LimitlessTabs.vue';
import MediabankSelector from '@components/MediabankSelector.vue';

export default {
    name: 'ProductBrandEdit',
    components: {
        MediabankSelector,
        LimitlessTabs,
        SimpleTextEditor,
        LimitlessButton,
        LimitlessPageHeader,
        LimitlessPageHeaderButton,
    },
    props: {
        id: {
            type: [Number, String],
            default: 0,
        },
    },
    data: () => ({
        busy_loading: true,
        busy_saving: false,
        busy_translating: false,
        brand: null,
        languages: [],
    }),
    mounted() {
        this.getLanguages();
        this.getBrand();
    },
    methods: {
        getBrand() {
            this.busy_loading = true;
            apiClient.get('/vue/product_brands/' + this.id + '')
                .then((response) => {
                    this.brand = response.data;
                })
                .finally(() => {
                    this.busy_loading = false;
                });
        },
        async saveBrand() {
            const isValid = await this.$refs.brandform.validate();
            if (isValid) {
                this.busy_saving = true;
                await apiClient.post('/vue/product_brands/' + this.id, this.brand)
                    .then((response) => {
                        if (parseInt(this.id, 10) !== parseInt(response.data.id, 10)) {
                            window.location.href = '/product_brands/' + response.data.id;
                        }
                        this.brand = response.data;
                        this.$refs.btnsavebrand.triggerSaved();
                    })
                    .catch((error) => {
                        const parsederror = getError(error);
                        if (typeof parsederror === 'object') {
                            this.$refs.brandform.setErrors(parsederror);
                        }
                        this.$refs.btnsavebrand.triggerFailed();
                    })
                    .finally(() => {
                        this.busy_saving = false;
                    });
            }
        },
        async deleteBrand() {
            let doChange = false;
            await this.$swal({
                text: 'Är du säker att du vill radera denna varumärke?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Ja, ta bort!',
                cancelButtonText: 'Avbryt',
            }).then((result) => {
                if (result.isConfirmed) {
                    doChange = true;
                }
            });
            if (doChange) {
                this.busy_saving = true;
                apiClient.delete('/vue/product_brands/' + this.id)
                    .then(() => {
                        window.location.href = '/product_brands';
                        this.busy_saving = false;
                    })
                    .catch(() => {
                        this.$refs.btndeletebrand.triggerFailed();
                    })
                    .finally(() => {
                        this.busy_saving = false;
                    });
            }
        },
        async translateTexts(lang) {
            this.busy_translating = true;
            const postData = {
                from: 'sv',
                to: lang.toLowerCase(),
                json: {
                    description: this.brand.translations.SV.description,
                },
                options: {
                    tag_handling: 'html',
                },
            };
            await apiClient.post('/vue/translate/openai', postData)
                .then((response) => {
                    this.brand.translations[lang].description = response.data.result.description;
                    this.$refs['btntranslate' + lang.toLowerCase()][0].triggerSaved();
                })
                .catch(() => {
                    this.$refs['btntranslate' + lang.toLowerCase()][0].triggerFailed();
                })
                .finally(() => {
                    this.busy_translating = false;
                });
        },
        getLanguages() {
            BackendRepository.getLanguages().then((res) => {
                this.languages = res;
            });
        },
    },
    computed: {
        brand_name() {
            return (this.brand && this.brand.id > 0 ? this.brand.name : 'Nytt varumärke');
        },
        brand_id() {
            return (this.brand && this.brand.id > 0 ? this.brand.id : '');
        },
        language_tabs() {
            if (!this.brand) {
                return [];
            }
            return this.languages.filter((lang) => lang.short !== 'SV' && lang.short !== 'SE').map((lang) => {
                let icon = null;
                if (!(this.brand.translations[lang.short] && this.brand.translations[lang.short].name)) {
                    icon = 'icon-warning';
                }
                return {
                    id: lang.short,
                    label: lang.name,
                    slot: 'slot-' + lang.short,
                    icon_after: icon,
                };
            });
        },
    },
};

</script>
